// autolayout/index - S48.2a / S48.2b / S48.2c.
// Orchestrators: convierten una plantilla tipada (layoutBase) + un dato
// real (producto o bundle) en un array de Field shapes listos para
// useEtiquetaEditor.setFields().
//
// Funciones principales:
//   - generarCamposTecnicosConAutoLayout(producto, layoutBase, logosConfig)
//       Para tecnica_individual (S48.2a). Lee producto.descripcion y
//       producto.marca para autolayout.
//   - generarCamposTecnicosBundleConAutoLayout(bundle, layoutBase, logosConfig)
//       Para tecnica_bundle (S48.2b). Lee bundle.items_resumen como fuente
//       tanto de descripcion (alias) como de marcas (resolverLogosFromText).
//
// S48.2c agrega:
//   - Font-size dinamico via calcularFontSize, propagado a medirBloque
//     para que el ajuste de hMm de descripcion respete el tamano que
//     realmente cabe sin desbordar.
//   - _hMmAjustadoLogos: agranda hMm cuando los logos NO caben en una
//     sola fila al ancho disponible (envuelve a 2+ filas).
//   - _wMmAjustadoLogos respeta el ancho maximo del bloque (anchoMm).
//
// Los orchestrators NO mutan los datos. Los valores reales se resuelven
// en dynamicFields.js al momento de render (resolveBindValue).
import { dividirCompatibilidadEnLineas } from './compatibilidad';
import { calcularFontSize } from './fontsize';
import { resolverLogos } from './logos';
import { resolverLogosFromText } from './logos_text';
import { medirBloque } from './measure';
import { findDynamicField } from '../dynamicFields';
import { newFieldId, snapMm, clampMm, findSize } from '../utils';

const LOGO_W_MM_PER_ITEM = 12;

function _bindKeyForFieldKey(fieldKey, meta) {
  if (meta && meta.bindKey) return meta.bindKey;
  return fieldKey;
}

function _typeForFieldKey(fieldKey, meta) {
  if (meta && meta.type) return meta.type;
  return 'text';
}

function _hMmAjustado(entry, lineas, fontSize) {
  const baseH = Number(entry.hMm) || 0;
  if (!Array.isArray(lineas) || lineas.length === 0) return baseH;
  const fs = Number(fontSize) || Number(entry.fontSize) || 7;
  const m = medirBloque(lineas, fs);
  return Math.max(baseH, m.hMm);
}

function _wMmAjustadoLogos(entry, logosCount, anchoMm) {
  const baseW = Number(entry.wMm) || 0;
  if (logosCount <= 1) return baseW;
  const needed = logosCount * LOGO_W_MM_PER_ITEM;
  const cap = Number(anchoMm) > 0 ? Number(anchoMm) : needed;
  return Math.min(cap, Math.max(baseW, needed));
}

function _hMmAjustadoLogos(entry, logosCount, anchoMm) {
  const baseH = Number(entry.hMm) || 0;
  if (logosCount <= 0) return baseH;
  const wMax = Number(anchoMm) > 0 ? Number(anchoMm) : (logosCount * LOGO_W_MM_PER_ITEM);
  const fits = Math.max(1, Math.floor(wMax / LOGO_W_MM_PER_ITEM));
  if (logosCount <= fits) return baseH;
  const rows = Math.ceil(logosCount / fits);
  return baseH * rows;
}

function _buildField(entry, sz, wMm, hMm, meta) {
  const xMm = clampMm(snapMm(Number(entry.xMm) || 0), 0, Math.max(0, sz.wMm - wMm));
  const yMm = clampMm(snapMm(Number(entry.yMm) || 0), 0, Math.max(0, sz.hMm - hMm));
  return {
    id: newFieldId(),
    type: _typeForFieldKey(entry.fieldKey, meta),
    bindKey: _bindKeyForFieldKey(entry.fieldKey, meta),
    xMm: xMm,
    yMm: yMm,
    wMm: wMm,
    hMm: hMm,
  };
}

export function generarCamposTecnicosConAutoLayout(producto, layoutBase, logosConfig) {
  if (!Array.isArray(layoutBase) || layoutBase.length === 0) return [];
  const prod = producto || {};
  const sizeId = 'tecnica-100x50';
  const sz = findSize(sizeId);

  const desc = prod.descripcion || '';
  const compatLines = dividirCompatibilidadEnLineas(desc, 92);
  const logosArr = resolverLogos(prod.marca, logosConfig);
  const logosCount = logosArr.length;

  const out = [];
  for (let i = 0; i < layoutBase.length; i += 1) {
    const entry = layoutBase[i];
    if (!entry || !entry.fieldKey) continue;
    const meta = findDynamicField(entry.fieldKey);
    let wMm = Number(entry.wMm) || (meta ? meta.defaultWMm : 30);
    let hMm = Number(entry.hMm) || (meta ? meta.defaultHMm : 6);
    if (entry.fieldKey === 'producto.descripcion') {
      const fsDin = calcularFontSize(compatLines, wMm, hMm);
      hMm = _hMmAjustado(entry, compatLines, fsDin);
    }
    if (entry.fieldKey === 'producto.logos') {
      const wAnt = wMm;
      wMm = _wMmAjustadoLogos(entry, logosCount, sz.wMm);
      hMm = _hMmAjustadoLogos(entry, logosCount, wAnt);
    }
    out.push(_buildField(entry, sz, wMm, hMm, meta));
  }
  return out;
}

export function generarCamposTecnicosBundleConAutoLayout(bundle, layoutBase, logosConfig) {
  if (!Array.isArray(layoutBase) || layoutBase.length === 0) return [];
  const b = bundle || {};
  const sizeId = 'tecnica-100x50';
  const sz = findSize(sizeId);

  // items_resumen alimenta tanto la descripcion (alias) como la deteccion
  // de marcas (substring word-boundary).
  const itemsTxt = b.items_resumen || '';
  const compatLines = dividirCompatibilidadEnLineas(itemsTxt, 92);
  const logosArr = resolverLogosFromText(itemsTxt, logosConfig);
  const logosCount = logosArr.length;

  const out = [];
  for (let i = 0; i < layoutBase.length; i += 1) {
    const entry = layoutBase[i];
    if (!entry || !entry.fieldKey) continue;
    const meta = findDynamicField(entry.fieldKey);
    let wMm = Number(entry.wMm) || (meta ? meta.defaultWMm : 30);
    let hMm = Number(entry.hMm) || (meta ? meta.defaultHMm : 6);
    if (entry.fieldKey === 'bundle.descripcion') {
      const fsDin = calcularFontSize(compatLines, wMm, hMm);
      hMm = _hMmAjustado(entry, compatLines, fsDin);
    }
    if (entry.fieldKey === 'bundle.logos') {
      const wAnt = wMm;
      wMm = _wMmAjustadoLogos(entry, logosCount, sz.wMm);
      hMm = _hMmAjustadoLogos(entry, logosCount, wAnt);
    }
    out.push(_buildField(entry, sz, wMm, hMm, meta));
  }
  return out;
}
